import { useEffect, useState } from 'react';
import axios from 'axios';
import { fetchLocationStaff } from '../../../actions/location/LocationActions';
import { LocationPagination, LocationStaffFilters, LocationStaffMember } from '../../../model/location';

const HTTP_STATUS_403_FORBIDDEN = 403;

const FORBIDDEN_MESSAGE = "You don't have access to this location's staff.";
const GENERIC_ERROR_MESSAGE = 'Unable to load staff. Please try again.';

export interface LocationStaffState {
    staff: Array<LocationStaffMember>;
    pagination: LocationPagination | null;
    loading: boolean;
    error?: string;
}

const toErrorMessage = (apiError: unknown): string =>
    axios.isAxiosError(apiError) && apiError.response?.status === HTTP_STATUS_403_FORBIDDEN
        ? FORBIDDEN_MESSAGE
        : GENERIC_ERROR_MESSAGE;

/**
 * Loads one page of a location's staff whenever the location or filters change.
 * Responses of superseded requests are ignored so only the latest result is shown.
 */
export const useLocationStaff = (
    locationId: number | undefined,
    { status, name, page, size }: LocationStaffFilters
): LocationStaffState => {
    const [state, setState] = useState<LocationStaffState>({ staff: [], pagination: null, loading: true });

    useEffect(() => {
        if (locationId === undefined) return;

        let isCurrent = true;
        setState(prev => ({ ...prev, loading: true, error: undefined }));

        fetchLocationStaff(locationId, { status, name, page, size })
            .then(result => {
                if (isCurrent) {
                    setState({ staff: result.staff, pagination: result.pagination, loading: false });
                }
            })
            .catch((apiError: unknown) => {
                if (isCurrent) {
                    setState({ staff: [], pagination: null, loading: false, error: toErrorMessage(apiError) });
                }
            });

        return () => {
            isCurrent = false;
        };
    }, [locationId, status, name, page, size]);

    return state;
};

export default useLocationStaff;
